import React from 'react';
import {
  StyledFilter,
  StyledFilterContainer,
  StyledFloatingFilter,
  StyledRootContainer,
  StyledSortByPriceMobileContainer,
  StyledTotalFilters,
  styles,
} from './RefinementPanelToggle.styles';
import { Box, BoxProps } from '@withjoy/joykit';
import { Filter } from '@withjoy/joykit/icons';

import { useRefinementPanelToggle, useRefinementPanelToggleController } from './RefinementPanelToggle.controller';
import { useTranslation } from '@shared/core';
import { SortByPriceDesktop } from '../SortByPrice/SortByPriceDesktop';
import { SortByPriceMobile } from '../SortByPrice/SortByPriceMobile';
import { ProductCatalogSearchQueryOrderBy } from '@graphql/generated';

export interface RefinementPanelToggleCommonProps {
  sortBy?: ProductCatalogSearchQueryOrderBy;
  handleOnSortChange?: (value: ProductCatalogSearchQueryOrderBy) => void;
}

export interface RefinementPanelToggleProps
  extends RefinementPanelToggleCommonProps,
    Readonly<{
      isMobileOrTablet?: boolean;
      defaultAlign?: string;
      isAdminView?: boolean;
      marginBottom?: string | number;
    }> {}

type RefinementPanelToggleBodyProps = {
  isFilterVisible: boolean;
  totalFilterSelected: number;
  isMobileOrTablet: boolean;
  isFixedTop?: boolean;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RefinementPanelToggleBody: React.FC<RefinementPanelToggleBodyProps & BoxProps<any>> = ({ isFilterVisible, totalFilterSelected, isMobileOrTablet, isFixedTop, ...rest }) => {
  const { t } = useTranslation('catalogRegistry');
  const tRefinementsPanel = t('refinementsPanel');

  const showHideMessage = isFilterVisible
    ? totalFilterSelected !== 1
      ? tRefinementsPanel.hideFilters()
      : tRefinementsPanel.hideFilter()
    : totalFilterSelected !== 1
      ? tRefinementsPanel.showFilters()
      : tRefinementsPanel.showFilter();

  const filterLabel = totalFilterSelected !== 1 ? tRefinementsPanel.filters() : tRefinementsPanel.filter();

  const showHideFiltersLabel = isFixedTop || !isMobileOrTablet ? showHideMessage : filterLabel;

  return (
    <Box
      data-togglemethod={isMobileOrTablet ? 'fab' : 'topNav'}
      {...rest}
      display="flex"
      flexDirection="row"
      alignItems="center"
      cursor="pointer"
      __css={{ whiteSpace: 'nowrap' }}
    >
      {!totalFilterSelected ? (
        <Filter />
      ) : (
        <StyledTotalFilters
          as="span"
          __css={styles.totalFilters({ isMobileOrTablet })}
        >
          {totalFilterSelected}
        </StyledTotalFilters>
      )}
      <Box
        __css={{ marginLeft: [3] }}
        typographyVariant="hed1"
      >
        {showHideFiltersLabel}
      </Box>
    </Box>
  );
};

export const RefinementPanelHeaderToggle = ({ isMobileOrTablet, sortBy, handleOnSortChange, marginBottom = 6 }: RefinementPanelToggleProps) => {
  const { handleFilterClick, isFilterVisible, totalFilterSelected } = useRefinementPanelToggle();
  const { refinementPanelToggleContainerRef, isFixedTop } = useRefinementPanelToggleController({ isMobileOrTablet });

  return (
    <StyledRootContainer ref={refinementPanelToggleContainerRef}>
      <StyledFilterContainer
        __css={styles.filterContainer}
        marginBottom={marginBottom}
      >
        {!isFixedTop && (
          <>
            <SortByPriceDesktop
              onChange={handleOnSortChange}
              value={sortBy}
            />
            <StyledFilter
              role={'group'}
              __css={styles.filter}
            >
              <RefinementPanelToggleBody
                isFilterVisible={isFilterVisible}
                totalFilterSelected={totalFilterSelected}
                isMobileOrTablet={!!isMobileOrTablet}
                isFixedTop={false}
                onClick={handleFilterClick}
              />
            </StyledFilter>
          </>
        )}
      </StyledFilterContainer>
    </StyledRootContainer>
  );
};

export const RefinementPanelTopBarToggle = ({ isMobileOrTablet, sortBy, handleOnSortChange }: RefinementPanelToggleProps) => {
  const { handleFilterClick, isFilterVisible, totalFilterSelected } = useRefinementPanelToggle();
  const { refinementPanelToggleContainerRef, isFixedTop } = useRefinementPanelToggleController({ isMobileOrTablet });

  return (
    <StyledRootContainer
      ref={refinementPanelToggleContainerRef}
      __css={styles.rootContainer}
    >
      <StyledFilterContainer __css={styles.filterContainerFixed}>
        {isFixedTop && (
          <>
            <SortByPriceDesktop
              onChange={handleOnSortChange}
              value={sortBy}
            />
            <RefinementPanelToggleBody
              isFilterVisible={isFilterVisible}
              totalFilterSelected={totalFilterSelected}
              isMobileOrTablet={!!isMobileOrTablet}
              isFixedTop={true}
              onClick={handleFilterClick}
            />
          </>
        )}
      </StyledFilterContainer>
    </StyledRootContainer>
  );
};

export const FloatingRefinementPanelToggle = ({ sortBy, handleOnSortChange }: RefinementPanelToggleCommonProps) => {
  const { handleFilterClick, isFilterVisible, totalFilterSelected, isAdminView } = useRefinementPanelToggle();
  const isSelected = sortBy && sortBy !== ProductCatalogSearchQueryOrderBy.featured;
  return (
    <StyledFloatingFilter __css={styles.stickyContainer(isAdminView)}>
      <StyledFilter
        role={'group'}
        __css={styles.floatingFilter}
      >
        <RefinementPanelToggleBody
          isFilterVisible={isFilterVisible}
          totalFilterSelected={totalFilterSelected}
          onClick={handleFilterClick}
          isMobileOrTablet
        />
        <VerticalDivider />
        <StyledSortByPriceMobileContainer __css={styles.sortByPriceMobileContainer(isSelected)}>
          <SortByPriceMobile
            onChange={handleOnSortChange}
            value={sortBy}
          />
        </StyledSortByPriceMobileContainer>
      </StyledFilter>
    </StyledFloatingFilter>
  );
};

export const VerticalDivider = () => <Box __css={styles.verticalDivider} />;
